import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { toTelDigits } from "../lib/phone";

const NAV_LINKS = [
  { id: "benefits", label: "Why Knockwave" },
  { id: "process", label: "Process" },
  { id: "earnings", label: "Earnings" },
  { id: "faq", label: "FAQ" },
];

export const Header = ({ phone }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-slate-200"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-24 md:h-32 flex items-center justify-between">
        <button
          data-testid="logo-link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 group"
          aria-label="Knockwave Telecom — Home"
        >
          <img
            src="/knockwave-logo.png"
            alt="Knockwave Telecom"
            className="h-20 md:h-24 w-auto object-contain"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-${link.id}`}
              onClick={() => scrollTo(link.id)}
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="header-call-btn"
            href={`tel:${toTelDigits(phone)}`}
            className="hidden md:inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-md transition-all hover:-translate-y-0.5"
          >
            <Phone className="w-4 h-4" />
            Apply Now
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-slate-900"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-b border-slate-200"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`mobile-nav-${link.id}`}
                onClick={() => scrollTo(link.id)}
                className="text-left py-3 text-base font-medium text-slate-700 hover:text-blue-600 border-b border-slate-100"
              >
                {link.label}
              </button>
            ))}
            <a
              data-testid="mobile-call-btn"
              href={`tel:${toTelDigits(phone)}`}
              className="mt-3 inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold px-4 py-3 rounded-md"
            >
              <Phone className="w-4 h-4" />
              Apply Now
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
